/**
 * Per-episode overrides for /content series episodes.
 *
 * The series files under `content/series/<slug>/season-N.json` are the
 * canonical source. The dashboard needs to let the admin EDIT existing
 * episodes (title, watch_url, download_url, thumbnail) before re-publishing
 * the season JSON to GitHub. Until the JSON is re-published, those edits
 * must also be reflected on the live site (series page + watch page).
 *
 * This module stores the edits in localStorage and exposes a tiny pub/sub
 * + a merge helper used by the Episodes tab and the public pages.
 */
import { useEffect, useState } from "react";
import { getSeasonsForSeries } from "./content/loader";
import type { ContentSeason, ContentEpisode, WatchQuality } from "./content/schema";

export type EpisodeOverride = {
  title?: string;
  watch_url?: string;
  watch_token?: string;
  watch_urls?: WatchQuality[];
  download_url?: string;
  thumbnail?: string;
  duration?: string;
};

export function cleanEpisodeOverride(patch: EpisodeOverride): EpisodeOverride {
  const cleanPatch: EpisodeOverride = {};
  (Object.keys(patch) as (keyof EpisodeOverride)[]).forEach((kk) => {
    const v = patch[kk];
    if (v === undefined) return;
    if (typeof v === "string" && v === "") return; // fall back to canonical
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (cleanPatch as any)[kk] = v;
  });
  return cleanPatch;
}

const KEY = "rawan-episode-overrides-v1";

type Map = Record<string, EpisodeOverride>;
type Listener = () => void;
const listeners = new Set<Listener>();

function read(): Map {
  if (typeof window === "undefined") return {};
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Map) : {};
  } catch {
    return {};
  }
}
function write(next: Map) {
  localStorage.setItem(KEY, JSON.stringify(next));
  listeners.forEach((l) => l());
}

function k(slug: string, season: number, number: number) {
  return `${slug}__s${season}__e${number}`;
}

export function getOverride(slug: string, season: number, number: number): EpisodeOverride | undefined {
  return read()[k(slug, season, number)];
}

export function setOverride(slug: string, season: number, number: number, patch: EpisodeOverride) {
  const all = read();
  const key = k(slug, season, number);
  // Only apply fields that were explicitly provided in the patch. Passing
  // `undefined` used to spread over an existing saved value and then get
  // dropped by JSON.stringify — silently deleting previously saved
  // watch_urls / watch_token on the next save. Filter undefined out first.
  const cleanPatch = cleanEpisodeOverride(patch);
  const next: EpisodeOverride = { ...(all[key] ?? {}), ...cleanPatch };
  if (Object.keys(next).length === 0) {
    delete all[key];
  } else {
    all[key] = next;
  }
  write(all);
}

export function clearOverride(slug: string, season: number, number: number) {
  const all = read();
  delete all[k(slug, season, number)];
  write(all);
}

export function useEpisodeOverrides(): Map {
  const [, setTick] = useState(0);
  useEffect(() => {
    const l: Listener = () => setTick((n) => n + 1);
    listeners.add(l);
    return () => { listeners.delete(l); };
  }, []);
  return read();
}

/** Apply overrides to a single content episode (does not mutate input). */
export function applyOverrideToEpisode(slug: string, season: number, ep: ContentEpisode): ContentEpisode {
  const o = getOverride(slug, season, ep.episode_number);
  if (!o) return ep;
  return {
    ...ep,
    title: o.title ?? ep.title,
    thumbnail: o.thumbnail ?? ep.thumbnail,
    duration: o.duration ?? ep.duration,
    watch_url: o.watch_url ?? ep.watch_url,
    watch_token: o.watch_token ?? ep.watch_token,
    download_url: o.download_url ?? ep.download_url,
    watch_urls: o.watch_urls ?? ep.watch_urls,
  };
}

/** Return seasons for a series with per-episode overrides applied. */
export function getMergedSeasonsForSeries(slug: string): ContentSeason[] {
  return getSeasonsForSeries(slug).map((s) => ({
    ...s,
    episodes: s.episodes.map((e) => applyOverrideToEpisode(slug, s.season, e)),
  }));
}
